/**
 * Call Guide — structured call companion for agents (they explain; the client decides).
 *
 * Data sources (all loaded in parallel on mount):
 *   · Portfolio summary → client balance, yield, upcoming maturities
 *   · Recent interactions → last contact date / outcome
 *   · Available products → opportunity gaps
 *   · Open cases → case selector for interaction logging
 *
 * Flow:
 *   1. Agent navigates here from the client roster / review queue.
 *      client name / email passed via React Router location state.
 *   2. Agent works through the accordion talking-point sections.
 *   3. After the call, agent logs it: select case, pick outcome,
 *      add notes + commitment + next-action → POST start + PATCH finalize.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import WithdrawalRetentionGuide from '../../components/agent/WithdrawalRetentionGuide';
import {
  AWAITING_ALLOCATION_TEXT,
  DESCRIBE_SERIES_TEXT,
  EXIT_TERMS_TEXT,
  ONBOARDING_HELP_TEXTS,
  SALES_CALL_DISCLAIMER_TEXT,
  SIGN_IN_HELP_TEXTS,
  TAX_QUESTIONS_TEXT,
} from '../../components/agent/callGuideTexts';
import interactionsService, {
  type ClientCase,
  type InteractionKind,
  type InteractionOutcome,
} from '../../services/interactions.service';
import portfolioService from '../../services/portfolio.service';
import workQueueService from '../../services/workQueue.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, formatMoney } from '../../lib/datetime';
import { calendarDate } from '../../components/client/enteringFund.helpers';
import { completeAfter, openTermKey } from '../../components/client/openTerms.helpers';
import { pickCurrencyBalance } from '../../lib/portfolioSummary.helpers';
import { humanize } from '../../lib/stringCase';
import { colors, gradients } from '../../styles/theme';
import type { PortfolioSummary } from '../../types/finance';

// ─── Types ────────────────────────────────────────────────────────

interface RecentInteraction {
  id: string;
  kind: string;
  outcome: string;
  notes: string | null;
  next_action_required: string | null;
  started_at: string;
  actor_name: string | null;
}

interface LocationState {
  clientName?: string;
  clientEmail?: string;
}

interface ExpiredKycDoc {
  field_key: string;
  label: string;
  expires_at: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────

function elapsed(startMs: number): string {
  const s = Math.floor((Date.now() - startMs) / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function kindLabel(k: string): string {
  return ({
    phone_outbound: 'Outbound call',
    phone_inbound: 'Inbound call',
    email_outbound: 'Email sent',
    email_inbound: 'Email received',
  } as Record<string, string>)[k] ?? k;
}

function outcomeLabel(o: string): string {
  return ({
    connected: 'Connected',
    voicemail_left: 'Voicemail left',
    no_answer: 'No answer',
    busy: 'Busy',
    wrong_number: 'Wrong number',
    client_hung_up: 'Client hung up',
    callback_scheduled: 'Callback scheduled',
  } as Record<string, string>)[o] ?? o;
}

// ─── Accordion section ────────────────────────────────────────────

interface SectionProps {
  title: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ title, badge, children, defaultOpen = false }: Readonly<SectionProps>): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 8,
    }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: open ? `${colors.navy}08` : colors.surface,
          border: 'none',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: 14,
          color: colors.ink,
          textAlign: 'left',
          transition: 'background 0.12s',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {badge && (
            <span style={{
              fontSize: 11, fontWeight: 500,
              background: colors.gold, color: colors.navy,
              borderRadius: 4, padding: '1px 6px',
            }}>{badge}</span>
          )}
        </span>
        <span style={{ fontSize: 12, color: colors.textMuted }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '14px 16px', background: colors.surface }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Talking-point item ───────────────────────────────────────────

function TalkingPoint({ text }: Readonly<{ text: string }>): JSX.Element {
  const [checked, setChecked] = useState(false);
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '7px 0',
      cursor: 'pointer',
      opacity: checked ? 0.45 : 1,
      transition: 'opacity 0.15s',
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => setChecked(e.target.checked)}
        style={{ marginTop: 3, accentColor: colors.navy, flexShrink: 0 }}
      />
      <span style={{ fontSize: 14, color: colors.inkSoft, textDecoration: checked ? 'line-through' : 'none' }}>
        {text}
      </span>
    </label>
  );
}

// ─── Page ─────────────────────────────────────────────────────────

/** Close the originating callback row after logging the call. Best-effort: a
 *  failure here must not undo the interaction that is already recorded. Returns
 *  false when the row stayed open, so the agent is told why it is still in
 *  their queue instead of meeting it again as work already done. */
async function completeCallbackBestEffort(callbackRequestId: string): Promise<boolean> {
  try {
    await workQueueService.completeCallback(callbackRequestId);
    return true;
  } catch (cbErr) {
    console.warn('callback-request complete failed (interaction still recorded)', cbErr);
    return false;
  }
}

type CallContextResults = [
  PromiseSettledResult<PortfolioSummary>,
  PromiseSettledResult<{ data: { items: RecentInteraction[] } }>,
  PromiseSettledResult<{ data: { items: ClientCase[] } }>,
];

/** Fetch the three context loads in parallel (each degrades independently). */
function fetchCallContext(clientId: string): Promise<CallContextResults> {
  return Promise.allSettled([
    portfolioService.getClientSummary(clientId),
    authService.getApi().get<{ items: RecentInteraction[] }>(
      `/api/v1/staff/clients/${clientId}/interactions?limit=5`,
    ),
    authService.getApi().get<{ items: ClientCase[] }>(
      `/api/v1/staff/clients/${clientId}/cases`,
    ),
  ]) as Promise<CallContextResults>;
}

/** Apply the settled results of the three context loads to the page state.
 *  Each load degrades independently; a failed summary surfaces the error. */
function applyCallContext(
  [sumRes, interRes, casesRes]: CallContextResults,
  handlers: {
    onSummary: (v: PortfolioSummary) => void;
    onInteractions: (v: RecentInteraction[]) => void;
    onCases: (v: ClientCase[]) => void;
    onError: (v: string) => void;
  },
): void {
  if (sumRes.status === 'fulfilled') handlers.onSummary(sumRes.value);
  if (interRes.status === 'fulfilled') handlers.onInteractions(interRes.value.data.items);
  if (casesRes.status === 'fulfilled') handlers.onCases(casesRes.value.data.items);
  if (sumRes.status === 'rejected') {
    handlers.onError(getApiErrorMessage(sumRes.reason, 'Could not load the client summary.'));
  }
}

interface SubmitCallLogArgs {
  readonly selectedCaseId: string;
  readonly callKind: InteractionKind;
  readonly outcome: InteractionOutcome;
  readonly notes: string;
  readonly nextAction: string;
  readonly commitment: string;
  readonly timerStart: number | null;
  readonly clientId: string;
  readonly callbackRequestId: string | null;
}

/** Persist the logged call: start + finalize the interaction, close the
 *  originating callback (best-effort), then return the refreshed recent
 *  interactions. Kept at module scope so the component callback stays thin. */
async function submitCallLog(
  args: SubmitCallLogArgs,
): Promise<{ items: RecentInteraction[]; callbackStillOpen: boolean }> {
  const { id: interactionId } = await interactionsService.start(args.selectedCaseId, {
    kind: args.callKind,
    started_at: args.timerStart ? new Date(args.timerStart).toISOString() : undefined,
  });
  await interactionsService.update(interactionId, {
    outcome: args.outcome,
    ended_at: new Date().toISOString(),
    notes: args.notes || undefined,
    next_action_required: args.nextAction || undefined,
    commitment_made: args.commitment || undefined,
  });
  // If we were launched from a pending-callback row on the agent dashboard,
  // close the loop so it drops off the Act-now queue.
  const callbackStillOpen = args.callbackRequestId
    ? !(await completeCallbackBestEffort(args.callbackRequestId))
    : false;
  const res = await authService.getApi().get<{ items: RecentInteraction[] }>(
    `/api/v1/staff/clients/${args.clientId}/interactions?limit=5`,
  );
  return { items: res.data.items, callbackStillOpen };
}

/** Shown after a logged call whose originating callback could not be closed. */
function CallbackWarning({ message }: Readonly<{ message: string | null }>): JSX.Element | null {
  if (!message) return null;
  return (
    <output style={{
      display: 'block',
      background: colors.warningBg, border: '1px solid #FCD34D',
      borderRadius: 7, padding: '10px 14px', color: colors.ink, fontSize: 13,
    }}>
      {message}
    </output>
  );
}

/** Shown when the expired-document check failed, so an empty reminder is never
 *  read as "this client is current". */
function KycCheckFailedNote({ show }: Readonly<{ show: boolean }>): JSX.Element | null {
  if (!show) return null;
  return (
    <output style={{
      display: 'block',
      background: colors.warningBg, border: '1px solid #FCD34D',
      borderRadius: 14, padding: '12px 16px', fontSize: 13, color: colors.ink,
    }}>
      Could not check this client document status. Confirm their ID is current before
      discussing new products.
    </output>
  );
}

/** Navy portfolio snapshot card (balance / yield / products / open cases). */
function PortfolioSnapshotCard({
  summary,
  ccy,
  openCasesCount,
}: {
  readonly summary: PortfolioSummary | null;
  readonly ccy: string;
  readonly openCasesCount: number;
}): JSX.Element {
  // PORTAL-01: `summary.balances` is kept separate per currency — pick the
  // snapshot's display currency's row (see helper docstring).
  const balance = summary ? pickCurrencyBalance(summary.balances, ccy) : null;
  return (
    <div style={{ background: gradients.navy, borderRadius: 14, padding: '20px 22px', color: '#fff' }}>
      <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>
        Summary at a glance
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Total balance</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: colors.gold }}>
            {balance ? formatMoney(balance.total_balance, ccy) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Results to date</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: colors.goldLight }}>
            {balance ? formatMoney(balance.accumulated_yield, ccy) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Active products</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {summary?.active_products_count ?? '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 3 }}>Open cases</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {openCasesCount}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Call-duration timer: tracks start time, a mm:ss display, and start/stop/reset.
 *  Extracted from CallGuide so its branching doesn't inflate that component's
 *  cognitive complexity. */
function useCallTimer(): {
  timerStart: number | null;
  timerDisplay: string;
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
} {
  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [timerDisplay, setTimerDisplay] = useState('00:00');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback((): void => {
    if (timerStart !== null) return;
    const start = Date.now();
    setTimerStart(start);
    timerRef.current = setInterval(() => setTimerDisplay(elapsed(start)), 1000);
  }, [timerStart]);

  const stopTimer = useCallback((): void => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const resetTimer = useCallback((): void => {
    setTimerStart(null);
    setTimerDisplay('00:00');
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  return { timerStart, timerDisplay, startTimer, stopTimer, resetTimer };
}

/** Loads the client's call context (portfolio summary, recent interactions,
 *  open cases, expired-KYC docs) and owns the selected-case state. Extracted
 *  from CallGuide so the two data-loading effects don't inflate that
 *  component's cognitive complexity. */
function useCallContext(clientId: string | undefined): {
  summary: PortfolioSummary | null;
  recentInteractions: RecentInteraction[];
  setRecentInteractions: React.Dispatch<React.SetStateAction<RecentInteraction[]>>;
  openCases: ClientCase[];
  kycExpired: ExpiredKycDoc[];
  kycCheckFailed: boolean;
  loadError: string | null;
  selectedCaseId: string;
  setSelectedCaseId: React.Dispatch<React.SetStateAction<string>>;
} {
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [recentInteractions, setRecentInteractions] = useState<RecentInteraction[]>([]);
  const [openCases, setOpenCases] = useState<ClientCase[]>([]);
  const [kycExpired, setKycExpired] = useState<ExpiredKycDoc[]>([]);
  const [kycCheckFailed, setKycCheckFailed] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState('');

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await fetchCallContext(clientId);
        if (cancelled) return;
        applyCallContext(results, {
          onSummary: setSummary,
          onInteractions: setRecentInteractions,
          onCases: (cases) => {
            setOpenCases(cases);
            setSelectedCaseId(cases[0]?.case_id ?? '');
          },
          onError: setLoadError,
        });
      } catch (e: unknown) {
        if (!cancelled) setLoadError(getApiErrorMessage(e, 'Could not load client context.'));
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // KYC-currency reminder: the expired documents on this client, if any. An agent
  // aid that never blocks the call guide from rendering, but a failed check is
  // flagged instead of hidden: an empty list reads as "documents are current",
  // and the agent then runs the call on that assumption.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setKycCheckFailed(false);
    authService.getApi()
      .get<{ items: ExpiredKycDoc[] }>(`/api/v1/staff/clients/${clientId}/kyc-expiry`)
      .then((r) => { if (!cancelled) setKycExpired(r.data.items); })
      .catch((err: unknown) => {
        console.warn('kyc-expiry check failed', err);
        if (!cancelled) setKycCheckFailed(true);
      });
    return () => { cancelled = true; };
  }, [clientId]);

  return {
    summary, recentInteractions, setRecentInteractions,
    openCases, kycExpired, kycCheckFailed, loadError, selectedCaseId, setSelectedCaseId,
  };
}

/** Call-duration display + start/stop button. Extracted from CallGuide's render
 *  so its `running` branching doesn't count toward that component's complexity. */
function CallTimerBar({ timerStart, timerDisplay, onStart, onStop }: Readonly<{
  timerStart: number | null;
  timerDisplay: string;
  onStart: () => void;
  onStop: () => void;
}>): JSX.Element {
  const running = timerStart !== null;
  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          color: running ? colors.navy : colors.textMuted,
        }}>
          {timerDisplay}
        </div>
        {running && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: colors.success,
            background: colors.successBg, borderRadius: 4, padding: '2px 7px',
          }}>LIVE</span>
        )}
      </div>
      <button
        type="button"
        onClick={running ? onStop : onStart}
        style={{
          padding: '8px 18px',
          borderRadius: 8,
          border: 'none',
          background: running ? colors.dangerBg : gradients.navy,
          color: running ? colors.danger : '#fff',
          fontWeight: 600,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        {running ? 'Stop timer' : 'Start call timer'}
      </button>
    </div>
  );
}

export default function CallGuide(): JSX.Element {
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state ?? {}) as LocationState;
  const clientName = state.clientName ?? 'Client';
  const clientEmail = state.clientEmail ?? undefined;
  // When this page is opened from the Agent Dashboard's "Log call"
  // button on a pending callback row, the URL carries ?callback={id}.
  // After successfully logging the interaction the page closes the
  // loop by marking that callback `completed` (non-voicemail outcomes)
  // — voicemail outcomes have their own inline path on the dashboard
  // and never route here, so we don't have to branch on outcome.
  const callbackRequestId = searchParams.get('callback');

  // Data + selected case (loading effects extracted into a hook)
  const {
    summary, recentInteractions, setRecentInteractions,
    openCases, kycExpired, kycCheckFailed, loadError, selectedCaseId, setSelectedCaseId,
  } = useCallContext(clientId);

  // Call logger state
  const [callKind, setCallKind] = useState<InteractionKind>('phone_outbound');
  const [outcome, setOutcome] = useState<InteractionOutcome>('connected');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [commitment, setCommitment] = useState('');
  const [logError, setLogError] = useState<string | null>(null);
  const [logSuccess, setLogSuccess] = useState(false);
  const [callbackWarning, setCallbackWarning] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);

  // Timer (state + start/stop/reset extracted into a hook)
  const { timerStart, timerDisplay, startTimer, stopTimer, resetTimer } = useCallTimer();

  const handleLogCall = useCallback(async (): Promise<void> => {
    if (!selectedCaseId) { setLogError('Select a case to associate this call with.'); return; }
    if (!clientId) return;
    setLogging(true);
    setLogError(null);
    setCallbackWarning(null);
    try {
      const { items, callbackStillOpen } = await submitCallLog({
        selectedCaseId, callKind, outcome, notes, nextAction, commitment,
        timerStart, clientId, callbackRequestId,
      });
      stopTimer();
      resetTimer();
      setLogSuccess(true);
      setCallbackWarning(
        callbackStillOpen
          ? 'The call was logged, but the callback request could not be closed. It may still appear in your queue; close it from the dashboard.'
          : null,
      );
      setNotes('');
      setNextAction('');
      setCommitment('');
      setRecentInteractions(items);
    } catch (e: unknown) {
      setLogError(getApiErrorMessage(e, 'Could not log the interaction.'));
    } finally {
      setLogging(false);
    }
  }, [selectedCaseId, callKind, outcome, notes, nextAction, commitment, timerStart, clientId, callbackRequestId, stopTimer, resetTimer, setRecentInteractions]);

  if (!clientId) return <p style={{ color: colors.danger }}>No client ID in URL.</p>;

  const lastInteraction = recentInteractions[0] ?? null;
  const ccy = summary?.upcoming_maturities[0]?.currency_code ?? 'MXN';

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <PageHeader
        title={`Call Guide — ${clientName}`}
        subtitle={clientEmail}
      />

      {loadError && (
        <div style={{
          background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
          borderRadius: 8, padding: '10px 14px', color: colors.danger,
          fontSize: 13, marginBottom: 16,
        }}>{loadError}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-5 items-start">

        {/* ── Left column: client snapshot ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* KYC update reminder — first thing the agent sees, so they ask the
              client to re-submit the lapsed document(s) on this call. */}
          <KycCheckFailedNote show={kycCheckFailed} />
          {kycExpired.length > 0 && (
            <div style={{
              background: colors.warningBg,
              border: '1px solid #FCD34D',
              borderRadius: 14,
              padding: '16px 18px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.warning, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                ⚠ KYC update needed
              </div>
              <p style={{ fontSize: 13, color: colors.ink, margin: '0 0 8px' }}>
                {kycExpired.length > 1
                  ? 'Ask the client to upload the expired documents below again.'
                  : 'Ask the client to upload the expired document below again.'}{' '}
                {'Until they do, they cannot sign a new contract or start a new contribution plan. An expired ID also stops withdrawals and redemptions. Money for a Series they already hold is still received; a manager reviews it.'}
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: colors.inkSoft }}>
                {kycExpired.map((d) => (
                  <li key={d.field_key} style={{ marginBottom: 2 }}>
                    <strong style={{ color: colors.ink }}>{d.label}</strong>
                    {d.expires_at ? ` — expired ${formatDate(d.expires_at)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Portfolio snapshot card */}
          <PortfolioSnapshotCard summary={summary} ccy={ccy} openCasesCount={openCases.length} />

          {/* Upcoming maturities */}
          {summary && summary.upcoming_maturities.length > 0 && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '14px 16px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Terms ending
              </div>
              {summary.upcoming_maturities.slice(0, 3).map(m => (
                <div key={openTermKey(m)} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: 13,
                }}>
                  <div>
                    <div style={{ fontWeight: 500, color: colors.ink }}>{m.product_name}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>{completeAfter(m.maturity_date)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: colors.navy }}>{formatMoney(m.amount, m.currency_code)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Last contact */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: '14px 16px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              Last contact
            </div>
            {lastInteraction ? (
              <div style={{ fontSize: 13 }}>
                <div style={{ color: colors.ink, fontWeight: 500, marginBottom: 3 }}>
                  {kindLabel(lastInteraction.kind)} — {outcomeLabel(lastInteraction.outcome)}
                </div>
                <div style={{ color: colors.textMuted, fontSize: 12 }}>
                  {formatDateTime(lastInteraction.started_at)} · {lastInteraction.actor_name ?? 'Unknown'}
                </div>
                {lastInteraction.next_action_required && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px',
                    background: colors.warningBg,
                    borderRadius: 6, fontSize: 12, color: colors.warning,
                  }}>
                    Pending: {lastInteraction.next_action_required}
                  </div>
                )}
                {lastInteraction.notes && (
                  <div style={{ marginTop: 8, fontSize: 12, color: colors.inkSoft, fontStyle: 'italic' }}>
                    &quot;{lastInteraction.notes.slice(0, 120)}{lastInteraction.notes.length > 120 ? '…' : ''}&quot;
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: colors.textMuted }}>No prior interactions.</div>
            )}
          </div>

          {/* Open cases quick list */}
          {openCases.length > 0 && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '14px 16px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                Open cases
              </div>
              {openCases.map(c => (
                <div key={c.case_id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: 13,
                }}>
                  <div>
                    <div style={{ fontWeight: 500, color: colors.ink }}>{c.workflow_name}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted }}>{humanize(c.current_state_slug)}</div>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600,
                    background: c.status === 'awaiting_review' ? colors.infoBg : colors.surfaceMuted,
                    color: c.status === 'awaiting_review' ? colors.infoText : colors.textMuted,
                    borderRadius: 4, padding: '2px 6px', textTransform: 'uppercase',
                  }}>
                    {humanize(c.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right column: talking points + logger ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Call timer */}
          <CallTimerBar
            timerStart={timerStart}
            timerDisplay={timerDisplay}
            onStart={startTimer}
            onStop={stopTimer}
          />

          {/* Withdrawal retention — surfaces when the client has an open
              withdrawal (live release date) or, on a first withdrawal, the
              static cooling-off script. Retention talking-points always show. */}
          <WithdrawalRetentionGuide clientId={clientId} />

          {/* Talking points accordion */}
          <Section title="Opening" defaultOpen>
            {kycExpired.length > 0 && (
              <TalkingPoint text={`KYC: ask the client to upload ${kycExpired.map((d) => d.label).join(', ')} again (expired). Needed for a new contract; an expired ID also stops withdrawals and redemptions.`} />
            )}
            <TalkingPoint text="Confirm you have the client's full attention and a good time to talk." />
            <TalkingPoint text={`Introduce yourself and confirm you're speaking with ${clientName}.`} />
            <TalkingPoint text="Before any amount or status: ask them to tell you their full name and date of birth, and compare with their file. Never read their details back. If they do not match, give no information and log the call." />
            <TalkingPoint text="State the purpose of the call in one sentence." />
            <TalkingPoint text={SALES_CALL_DISCLAIMER_TEXT} />
            {lastInteraction?.next_action_required && (
              <TalkingPoint text={`Follow up on pending action: "${lastInteraction.next_action_required}"`} />
            )}
          </Section>

          <Section title="Onboarding help">
            {ONBOARDING_HELP_TEXTS.map((t) => <TalkingPoint key={t} text={t} />)}
          </Section>

          <Section title="Summary review" badge={summary ? `${summary.active_products_count} products` : undefined}>
            <TalkingPoint text="Review their current balance and confirm it matches their expectations." />
            {summary && summary.upcoming_maturities.length > 0 && (
              <TalkingPoint text={
                `Term ending: the current term in ${summary.upcoming_maturities[0].product_name} is complete after ` +
                `${calendarDate(summary.upcoming_maturities[0].maturity_date)}. The next term starts by itself — ask what they intend.`
              } />
            )}
            <TalkingPoint text="Review the results shared with them to date. They are their share of what each product actually earned, never a fixed rate." />
            <TalkingPoint text="Check if any deposits are pending approval or awaiting funds transfer." />
            <TalkingPoint text={AWAITING_ALLOCATION_TEXT} />
            {summary && summary.active_commitments_count > 0 && (
              <TalkingPoint text={
                `${summary.active_commitments_count} active commitment(s) — confirm recurring schedule is still correct.`
              } />
            )}
          </Section>

          <Section title="Needs discovery">
            <TalkingPoint text="What do they want their money to do in the next 12 months?" />
            <TalkingPoint text="Have their liquidity needs or risk tolerance changed since the last call? If so, write what changed in the call notes." />
            <TalkingPoint text="Do they expect a large expense that would change how much they can contribute?" />
            <TalkingPoint text="Do they have money to contribute beyond their emergency reserve?" />
          </Section>

          <Section title="Series open to them">
            <TalkingPoint text={DESCRIBE_SERIES_TEXT} />
            <TalkingPoint text="Walk them through the Simulator in their own portal (Simulator tab) with the amount they choose. Its figures are for informational purposes and are not promised rates: they receive their share of what the Series actually earns." />
            <TalkingPoint text="Explain the minimum contribution, when each term ends, and that the next term starts by itself." />
            <TalkingPoint text={EXIT_TERMS_TEXT} />
            <TalkingPoint text="Before they can contribute: their file is approved; living in Mexico, they have given their real RFC; they have registered a bank account of their own in the Series' currency; and for a US Series they have signed a W-8BEN or W-9 in the portal." />
            <TalkingPoint text="If they are interested, use '+ Propose a Series' on their client page. They review, sign and contribute from their own portal." />
            <TalkingPoint text="'+ Propose a Series' lists every Series. When you send it, the portal refuses one that is not open to this client. If it refuses, do not offer that Series and do not explain why. Canada: no Series is offered yet." />
            <TalkingPoint text="A Mexico resident buys a US Series only while outside the United States. If they are in the US, do not present it; the portal refuses the order." />
            <TalkingPoint text="A US resident answers a few questions and receives an information package before signing. Let them answer on their own." />
            <TalkingPoint text={TAX_QUESTIONS_TEXT} />
          </Section>

          <Section title="Objection handling">
            <TalkingPoint text="'Rates are too low' — bring it back to their goals and horizon. They become a partner and share the Series' real result; never compare a Series with bank accounts or government instruments." />
            <TalkingPoint text="'I need liquidity' — show them Series with shorter terms and explain that when a term ends the next starts by itself unless they withdraw. Check they keep a reserve elsewhere: capital withdrawn before its term ends earns nothing for that term." />
            <TalkingPoint text="'I'm not sure about the platform' — walk them through their Summary and their statements." />
            <TalkingPoint text="'I'll think about it' — agree a specific callback date and note it as next action." />
          </Section>

          <Section title="Sign-in help">
            {SIGN_IN_HELP_TEXTS.map((t) => <TalkingPoint key={t} text={t} />)}
          </Section>

          <Section title="Closing">
            <TalkingPoint text="Summarise what was agreed and next steps." />
            <TalkingPoint text="Confirm the client's contact preferences and best callback time." />
            <TalkingPoint text="If they want a recap, it is in their portal. Any e-mail says only that the details are there when they sign in: no amounts, Series or references." />
            <TalkingPoint text="Log this interaction before moving to the next client." />
          </Section>

          {/* ── Call logger ── */}
          <div style={{
            marginTop: 4,
            border: `2px solid ${colors.navy}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            <div style={{
              background: gradients.navy,
              padding: '12px 18px',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}>
              Log this call
            </div>
            <div style={{ padding: '16px 18px', background: colors.surface, display: 'flex', flexDirection: 'column', gap: 14 }}>

              {logSuccess && (
                <div style={{
                  background: colors.successBg, border: `1px solid ${colors.successBorder}`,
                  borderRadius: 7, padding: '10px 14px', color: colors.successText, fontSize: 13,
                }}>
                  Call logged successfully.
                </div>
              )}
              <CallbackWarning message={callbackWarning} />
              {logError && (
                <div style={{
                  background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
                  borderRadius: 7, padding: '10px 14px', color: colors.danger, fontSize: 13,
                }}>
                  {logError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="callguide-page-call-type" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Call type
                  </label>
                  <select
                    id="callguide-page-call-type"
                    value={callKind}
                    onChange={e => setCallKind(e.target.value as InteractionKind)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13, background: colors.surface }}
                  >
                    <option value="phone_outbound">Outbound call</option>
                    <option value="phone_inbound">Inbound call</option>
                    <option value="email_outbound">Email sent</option>
                    <option value="email_inbound">Email received</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="callguide-page-outcome" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Outcome
                  </label>
                  <select
                    id="callguide-page-outcome"
                    value={outcome}
                    onChange={e => setOutcome(e.target.value as InteractionOutcome)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13, background: colors.surface }}
                  >
                    <option value="connected">Connected</option>
                    <option value="voicemail_left">Voicemail left</option>
                    <option value="no_answer">No answer</option>
                    <option value="busy">Busy</option>
                    <option value="wrong_number">Wrong number</option>
                    <option value="client_hung_up">Client hung up</option>
                    <option value="callback_scheduled">Callback scheduled</option>
                  </select>
                </div>
              </div>

              {openCases.length > 0 ? (
                <div>
                  <label htmlFor="callguide-page-case" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Associate with case
                  </label>
                  <select
                    id="callguide-page-case"
                    value={selectedCaseId}
                    onChange={e => setSelectedCaseId(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13, background: colors.surface }}
                  >
                    {openCases.map(c => (
                      <option key={c.case_id} value={c.case_id}>
                        {c.workflow_name} — {humanize(c.current_state_slug)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: colors.textMuted, padding: '8px 10px', background: colors.surfaceMuted, borderRadius: 7 }}>
                  No open cases found. Open a case from the review queue to link this interaction.
                </div>
              )}

              <div>
                <label htmlFor="callguide-page-notes" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Notes
                </label>
                <textarea
                  id="callguide-page-notes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  maxLength={10000}
                  placeholder="Key discussion points, client concerns, market context…"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '8px 10px', borderRadius: 7,
                    border: `1px solid ${colors.border}`, fontSize: 13,
                    resize: 'vertical', background: colors.surface,
                  }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="callguide-page-commitment" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Commitment made
                  </label>
                  <input
                    id="callguide-page-commitment"
                    type="text"
                    value={commitment}
                    onChange={e => setCommitment(e.target.value)}
                    maxLength={500}
                    placeholder="e.g. Client will transfer MXN 100K by Friday"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13 }}
                  />
                </div>
                <div>
                  <label htmlFor="callguide-page-next-action" style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Next action required
                  </label>
                  <input
                    id="callguide-page-next-action"
                    type="text"
                    value={nextAction}
                    onChange={e => setNextAction(e.target.value)}
                    maxLength={500}
                    placeholder="e.g. Call back Thursday to confirm transfer"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, border: `1px solid ${colors.border}`, fontSize: 13 }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleLogCall()}
                disabled={logging || openCases.length === 0}
                style={{
                  padding: '11px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: logging || openCases.length === 0 ? colors.disabledBg : gradients.navy,
                  color: logging || openCases.length === 0 ? colors.disabledText : '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: logging || openCases.length === 0 ? 'not-allowed' : 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                {logging ? 'Logging…' : 'Log call'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
